import React, {useEffect, useState} from 'react';
import {Home, Search, MessageCircle, MoreHorizontal} from 'lucide-react';
import {LuBellRing, LuMessagesSquare} from "react-icons/lu";
import {Link, useLocation, useNavigate} from "react-router-dom";

import {useNavVisibility} from '../NavVisibilityContext/NavVisibilityContext';
import {useNavCount} from "../../contexts/NavCountProvider";
import styles from './MainNav.module.css';
import {ImProfile} from "react-icons/im";
import {IoDocument, IoDocuments} from "react-icons/io5";
import {SiGoogleforms} from "react-icons/si";
import {FaUsers} from "react-icons/fa";
import {HiDocumentDuplicate, HiDocumentReport} from "react-icons/hi";
import {GrDocumentPerformance} from "react-icons/gr";
import useApi from "../../utils/useApi";
import PopoverNavItem from "./NavPopover/NavPopover";
import {GiPortal} from "react-icons/gi";
import {BsPersonBadge} from "react-icons/bs";
import {MdSpaceDashboard} from "react-icons/md";
import {FaBarsProgress} from "react-icons/fa6";

const MainNav = ({ user, customClass, showTour, setShowTour, onPopoverStateChange  }) => {
    const { getCount } = useApi();
    const location = useLocation();
    const navigate = useNavigate();
    const isSuperAdmin = user && (user.role === 'superadmin');
    const {
        messageCount,
        newDocs,
        newDocsCount,
        assignedDocs,
        assignedDocsCount,
        portalRequestCount,
        refreshCounts
    } = useNavCount();
    const { isNavVisible } = useNavVisibility();

    useEffect(() => {
        initializeAdminMenu();

    }, []);

    const initializeAdminMenu = async () => {
        refreshCounts();
    }

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }


    const handleNavigatePortal = () => {
        navigate('/portal-requests')
    }

    const handleNavigateMessages = () => {
        navigate('/messages')
    }

    let navItems = [];

    if (user?.user_type == 'admin') {
        navItems.push(
            { to: "/", icon: <MdSpaceDashboard/>, label: "Dashboard", onClick: handleScrollTop },
            { to: "/clients", icon: <BsPersonBadge />, label: "Clients", onClick: handleScrollTop },
            {
                to: "/profile",
                icon: <ImProfile/>,
                label: "My Profile",
                customClass: styles.hideOnMobile},
            {
                to: '/messages',
                icon: <LuMessagesSquare/>,
                label: "Messages",
                isPopover: true,
                type: 'messages',
                count: messageCount,
                dontPopover: true,
                onClick: handleNavigateMessages,
            },
            {
                icon: <HiDocumentDuplicate/>,
                label: "New Documents",
                isPopover: true,
                data: newDocs,
                type: 'newDocs',
                count: newDocsCount,
            },
            {
                icon: <GrDocumentPerformance/>,
                label: "In Progress",
                isPopover: true,
                data: assignedDocs,
                type: 'assignedDocs',
                count: assignedDocsCount,
            },
            {
                to: '/portal-requests',
                icon: <GiPortal />,
                label: "Portal Requests",
                isPopover: true,
                type: 'assignedDocs',
                count: portalRequestCount,
                dontPopover: true,
                onClick: handleNavigatePortal,
            },
            {
                to: "/reports", icon: <HiDocumentReport />,
                label: "Reports",

            },

        );
    }
    else {
        navItems.push(
            { to: "/", icon: <FaBarsProgress/>, label: "Dashboard", onClick: handleScrollTop },
            { to: "/profile", icon: <ImProfile/>, label: "My Profile" },
            { to: "/my-documents", icon: <IoDocuments/>, label: "My Documents" },
            { to: "/tax-questionnaire", icon: <SiGoogleforms/>, label: "Tax Questionnaire" },
            {
                to: '/messages',
                icon: <LuMessagesSquare/>,
                label: "Message My Preparer",
                isPopover: true,
                type: 'messages',
                count: messageCount,
                dontPopover: true,
                onClick: handleNavigateMessages,
            },
        );

        console.log(user);
    }

    navItems.push(
        { to: "/user-settings", icon: <MoreHorizontal/>, label: "Menu" },
    );

    const handleSearchPerformed = (query) => {
        navigate(`/search-all?q=${encodeURIComponent(query)}`);
    };

    return (
        <div id="MainNav" className={`${styles.MainNav} ${customClass ? customClass : (isNavVisible ? styles.visible : styles.hidden)} main-nav`}>
            {navItems.map((item, index) => {
                if (item.isPopover) {
                    return (
                        <PopoverNavItem
                            key={index}
                            icon={item.icon}
                            label={item.label}
                            data={item.data}
                            active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
                            onClick={item.onClick}
                            type={item.type}
                            count={item.count}
                            dontPopover={item?.dontPopover}
                            onPopoverStateChange={onPopoverStateChange}
                        />
                    );
                }

                return item.to ? (
                    <Link key={index} to={item.to} className={`${item.label.toLowerCase()} ${styles.navLink} ${item.customClass || ''}`}>
                        <NavItem
                            onClick={item.onClick}
                            icon={item.icon}
                            label={item.label}
                            active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
                            customClass={item.customClass}
                        />
                    </Link>
                ) : (
                           <div key={index} className={`${item.label.toLowerCase()} ${styles.navLink} ${item.customClass || ''}`}>
                               <NavItem
                                   onClick={item.onClick}
                                   icon={item.icon}
                                   label={item.label}
                                   active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
                                   customClass={item.customClass}
                               />
                           </div>
                       );
            })}
        </div>
    );
};

const NavItem = ({ icon, label, active, onClick, customClass }) => {
    return (
        <div
            className={`${(active ? styles.navItemActive : '')} ${styles.navItem} ${customClass || ''}`}
            onClick={onClick}>
            <div className={styles.navItemIconSpan}>
                {React.cloneElement(icon, { size: 24 })}
                <span className={styles.navItemSpan} style={{}}>{label}</span>
            </div>

            {active && (
                <div className={styles.navDot}/>
            )}
        </div>
    );
};

export default MainNav;
